import {
  Button,
  Card,
  Collapse,
  message,
  Skeleton,
  Space,
  Typography
} from "antd";
import React, { useEffect, useState } from "react";
import { GetSurveyTemplatesAdminResponse } from "../../../constants/apiRequestResponse";
import TEST_IDS from "../../../constants/cypressTestIds";
import { getSurveyTemplatesGrouped } from "../../../services/api";
import { getQueryParam, parseDate, setQueryParam } from "../../../util";
import SurveyTemplateForm from "./SurveyTemplateForm";

export type Mode = "view" | "create" | "hidden"
export default function SurveyTemplateTab() {
  const [getSurveyTemplatesAdminResponse, setGetSurveyTemplatesAdminResponse] = useState<GetSurveyTemplatesAdminResponse | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const [surveyTemplateId, setSurveyTemplateId] = useState<string | undefined>(getQueryParam("surveyTemplateId"));
  const [mode, setMode] = useState<Mode>(getQueryParam("mode") as Mode || "hidden");

  const selectedTemplate = surveyTemplateId ? getSurveyTemplatesAdminResponse?.bySurveyTemplateId[surveyTemplateId] : undefined;

  const getData = (ignoreCache: boolean) => {
    setIsLoading(true);
    getSurveyTemplatesGrouped(ignoreCache)
      .then((res) => {
        if (!res.success) throw new Error(res.message);
        setGetSurveyTemplatesAdminResponse(res);
      })
      .catch((err) => message.error(err.toString()))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    getData(false);
  }, []);

  useEffect(() => {
    if (!selectedTemplate) return

    // not allowed to create a survey template off an inactive survey template. override values
    const mismatch = !selectedTemplate.active && mode === "create"
    if (mismatch) {
      setMode("view")
      setQueryParam("mode", "view")
    }
  }, [selectedTemplate, mode])

  return (
    <div style={{ display: "flex", gap: 10, flexDirection: "column" }}>
      <Typography.Title level={4} style={{ margin: 0 }}>
        Survey Templates
      </Typography.Title>

      {isLoading ? (
        <Skeleton active />
      ) : (
        Object.entries(getSurveyTemplatesAdminResponse?.bySurveyTemplateType || {}).map(([type, group]) => {
          const hasPast = (group?.past?.length || 0) > 0;

          return (
            <Card key={type} style={{ marginBottom: 16 }}>
              {group.active && (
                <div style={{ marginBottom: hasPast ? 16 : 0, display: "flex", justifyContent: "space-between" }}>
                  <div>
                    <Typography.Text>{group.active.name}</Typography.Text>
                    <div>
                      <Typography.Text type="secondary">
                        {group.active.questions?.length || 0} questions · Created: {parseDate(group.active.createdAt)}
                      </Typography.Text>
                    </div>
                  </div>
                  <Space>
                    <Button
                      type="link"
                      data-testid={`${TEST_IDS.ADMIN_SURVEY_TEMPLATE_TAB.view_details_btn}_${type}`}
                      onClick={() => {
                        setMode("view")
                        setQueryParam("mode", "view")
                        setSurveyTemplateId(group.active?._id);
                        setQueryParam("surveyTemplateId", group.active?._id);
                      }}
                    >
                      View details
                    </Button>
                    <Button
                      type="primary"
                      data-testid={`${TEST_IDS.ADMIN_SURVEY_TEMPLATE_TAB.create_new_version_btn}_${type}`}
                      onClick={() => {
                        setMode("create")
                        setQueryParam("mode", "create")
                        setSurveyTemplateId(group.active?._id);
                        setQueryParam("surveyTemplateId", group.active?._id);
                      }}
                    >
                      Create a new version
                    </Button>
                  </Space>
                </div>
              )}

              {hasPast && (
                <Collapse ghost>
                  <Collapse.Panel
                    key="past"
                    header={<Typography.Text type="secondary">Past versions ({group.past.length})</Typography.Text>}
                  >
                    <Space direction="vertical" style={{ width: "100%" }}>
                      {group.past.map((template) => (
                        <div key={template._id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", maxWidth: 250, }}>
                          <Typography.Text type="secondary">Created on {parseDate(template.createdAt)}</Typography.Text>
                          <Button
                            type="link"
                            size="small"
                            data-testid={`${TEST_IDS.ADMIN_SURVEY_TEMPLATE_TAB.past_versions_view_details_btn}_${template.type}`}
                            onClick={() => {
                              setSurveyTemplateId(template._id);
                              setQueryParam("surveyTemplateId", template._id);
                              setMode("view")
                              setQueryParam("mode", "view")
                            }}
                          >
                            View details
                          </Button>
                        </div>
                      ))}
                    </Space>
                  </Collapse.Panel>
                </Collapse>
              )}
            </Card>
          );
        })
      )}

      <SurveyTemplateForm
        mode={mode}
        template={selectedTemplate}
        onClose={() => {
          setSurveyTemplateId(undefined);
          setQueryParam("surveyTemplateId", "");
          setMode("hidden");
          setQueryParam("mode", "")
        }}
        onSuccess={() => getData(true)}
      />
    </div>
  );
}
